/**
 * Push Notification Service
 * 
 * Handles device token registration and push notification setup
 * Supports FCM (Android) and APNs (iOS)
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

// API base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Current device token
let deviceToken = null;
let isRegistered = false;

// Notification callbacks
let onNotificationReceived = null;
let onNotificationAction = null;

/**
 * Get auth token from storage
 */
const getAuthToken = () => {
  return localStorage.getItem('boat360_token');
};

/**
 * Push Notification Service
 */
export const PushNotificationService = {
  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return isNative;
  },

  /**
   * Get platform type for registration
   */
  getPlatform() {
    return platform; // 'ios' or 'android'
  },

  /**
   * Check current permission status
   */
  async checkPermissions() {
    if (!isNative) {
      // Web push API
      if ('Notification' in window) {
        return { receive: Notification.permission };
      }
      return { receive: 'denied' };
    }

    try {
      const status = await PushNotifications.checkPermissions();
      return status;
    } catch (error) {
      console.error('[PushNotification] Permission check error:', error);
      return { receive: 'denied' };
    }
  },

  /**
   * Request push notification permissions
   */
  async requestPermissions() {
    if (!isNative) {
      // Web push API
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return { receive: permission };
      }
      return { receive: 'denied' };
    }

    try {
      const status = await PushNotifications.requestPermissions();
      console.log('[PushNotification] Permission status:', status);
      return status;
    } catch (error) {
      console.error('[PushNotification] Permission request error:', error);
      return { receive: 'denied' };
    }
  },

  /**
   * Register for push notifications and get device token
   */
  async register() {
    if (!isNative) {
      console.log('[PushNotification] Push not supported on web');
      return null;
    }

    try {
      // Request permissions first
      const permission = await this.requestPermissions();
      if (permission.receive !== 'granted') {
        console.log('[PushNotification] Permission not granted');
        return null;
      }

      // Set up listeners before registering
      await this.addListeners();

      // Register with APNS/FCM
      await PushNotifications.register();
      
      console.log('[PushNotification] Registration initiated');
      
      // Token will be received via listener
      return new Promise((resolve) => {
        // Wait for token (max 10 seconds)
        const timeout = setTimeout(() => {
          console.log('[PushNotification] Token timeout');
          resolve(deviceToken);
        }, 10000);

        const checkToken = setInterval(() => {
          if (deviceToken) {
            clearTimeout(timeout);
            clearInterval(checkToken);
            resolve(deviceToken);
          }
        }, 100);
      });
    } catch (error) {
      console.error('[PushNotification] Registration error:', error);
      return null;
    }
  },

  /**
   * Add push notification listeners
   */
  async addListeners() {
    if (!isNative) return;

    // Registration success
    await PushNotifications.addListener('registration', async (token) => {
      console.log('[PushNotification] Token received:', token.value.substring(0, 20) + '...');
      deviceToken = token.value;
      
      // Register token with backend
      await this.registerTokenWithBackend(token.value);
    });

    // Registration error
    await PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotification] Registration error:', error);
    });

    // Notification received while app is in foreground
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[PushNotification] Received:', notification);
      
      if (onNotificationReceived) {
        onNotificationReceived(notification);
      }
    });

    // User tapped on notification
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[PushNotification] Action:', action);
      
      if (onNotificationAction) {
        onNotificationAction(action);
      }
    });

    console.log('[PushNotification] Listeners added');
  },

  /**
   * Register device token with backend
   */
  async registerTokenWithBackend(token) {
    const authToken = getAuthToken();
    if (!authToken) {
      console.log('[PushNotification] No auth token, skipping backend registration');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/devices/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          platform: platform,
          token: token,
        }),
      });

      if (response.ok) {
        isRegistered = true;
        console.log('[PushNotification] Token registered with backend');
        return true;
      } else {
        console.error('[PushNotification] Backend registration failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[PushNotification] Backend registration error:', error);
      return false;
    }
  },

  /**
   * Unregister device from push notifications
   */
  async unregister() {
    const authToken = getAuthToken();
    
    if (authToken && deviceToken) {
      try {
        await fetch(`${API_BASE_URL}/api/devices/unregister`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            token: deviceToken,
          }),
        });
        console.log('[PushNotification] Unregistered from backend');
      } catch (error) {
        console.error('[PushNotification] Unregister error:', error);
      }
    }

    // Remove all listeners
    if (isNative) {
      await PushNotifications.removeAllListeners();
    }

    deviceToken = null;
    isRegistered = false;
    console.log('[PushNotification] Unregistered');
  },

  /**
   * Set callback for notification received
   */
  setOnNotificationReceived(callback) {
    onNotificationReceived = callback;
  },

  /**
   * Set callback for notification action
   */
  setOnNotificationAction(callback) {
    onNotificationAction = callback;
  },

  /**
   * Get current device token
   */
  getToken() {
    return deviceToken;
  },

  /**
   * Check if registered
   */
  isRegistered() {
    return isRegistered;
  },

  /**
   * Get delivered notifications (iOS badge count, etc.)
   */
  async getDeliveredNotifications() {
    if (!isNative) return [];

    try {
      const { notifications } = await PushNotifications.getDeliveredNotifications();
      return notifications;
    } catch (error) {
      console.error('[PushNotification] Get delivered error:', error);
      return [];
    }
  },

  /**
   * Remove all delivered notifications
   */
  async removeAllDeliveredNotifications() {
    if (!isNative) return;

    try {
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('[PushNotification] Cleared delivered notifications');
    } catch (error) {
      console.error('[PushNotification] Clear error:', error);
    }
  },
};

export default PushNotificationService;
